// Shared Israel calendar-day contract for every management date input whose backend stores a
// calendar day rather than an instant - the eligibility override's "תוקף עד", and the
// third-party payment authorization's validity window (HAT-1259).
//
// Those backends (see EligibilityLimitOverrideService.cs and
// ThirdPartyPaymentAuthorizationService.cs) evaluate the date against an Asia/Jerusalem
// calendar day. If the frontend used the browser's
// local time zone to translate ISO instants back and forth, a user in a non-IL time
// zone would see a different calendar day than the one the backend persisted — an
// amount-only edit could then silently drift the stored ExpiresAt or fail validation
// even though the user never touched the date input.
//
// All helpers below intentionally use Intl.DateTimeFormat with `timeZone: 'Asia/Jerusalem'`
// so the calendar day is the same for a caller in NY, UTC, or Israel.

const IL_TZ = 'Asia/Jerusalem';

const IL_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: IL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Formats a Date as an Israel calendar `yyyy-MM-dd` regardless of the browser's TZ.
 *
 * Assembled from `formatToParts` output to avoid any locale-side surprises
 * (RTL marks, non-hyphen separators, alternative calendars, …).
 */
export const formatIsraelDate = (d: Date): string => {
  const parts = IL_DATE_FORMATTER.formatToParts(d);
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

// Detects a full ISO date+time that carries no time-zone designator, e.g.
// "2028-03-15T20:30:00" or "2028-03-15T20:30:00.1234567". These come from .NET
// serializing a `DateTime` whose `Kind` is `Unspecified` — the exact shape a SQL
// `datetime2` column produces after being read back through EF without an
// explicit `SpecifyKind(Utc)` on the DTO. The `T` is required so that a plain
// `"2028-03-15"` (date-only) still passes through untouched.
const ISO_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

/**
 * Server-side ISO instant → the Israel calendar day the user should see.
 *
 * Defensive against legacy responses that arrive without a `Z` or `±HH:MM`
 * offset: `new Date("2028-03-15T20:30:00")` in a browser is parsed as *local*
 * time (browser TZ), which would make a coordinator in NY see March 16 for a
 * value the backend meant as March 15 UTC. Every eligibility-override
 * timestamp is UTC on the server, so we treat any suffixless ISO as UTC before
 * folding it to an Israel calendar day. The primary fix is that the backend
 * now emits `Kind = Utc` (see EligibilityLimitOverrideDtoOut) so the wire
 * string ends with `Z`; this branch is belt-and-suspenders for any callers
 * still emitting the older suffixless shape.
 */
export const parseExpiresAtFromServer = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const normalized = ISO_WITHOUT_TZ.test(iso) ? `${iso}Z` : iso;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return '';
  return formatIsraelDate(d);
};

/** Today's date on the Israel calendar as `yyyy-MM-dd`, safe to compare with `<`/`>` because both operands are `yyyy-MM-dd`. */
export const israelTodayIso = (now: Date = new Date()): string => formatIsraelDate(now);

/**
 * Suggested default expiration for the "create" flow, `now + monthsAhead` on the
 * Israel calendar. Clamps to the last valid day of the target month, matching
 * .NET `AddMonths` semantics so FE and BE fallbacks agree.
 */
export const getDefaultIsraelExpiresAt = (monthsAhead: number, now: Date = new Date()): string => {
  const todayIso = formatIsraelDate(now);
  const [yStr, mStr, dStr] = todayIso.split('-');
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10) - 1;
  const d = parseInt(dStr, 10);
  const total = m + monthsAhead;
  const targetYear = y + Math.floor(total / 12);
  const targetMonth = ((total % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const targetDay = Math.min(d, lastDay);
  const mm = String(targetMonth + 1).padStart(2, '0');
  const dd = String(targetDay).padStart(2, '0');
  return `${targetYear}-${mm}-${dd}`;
};
